import * as d3 from "d3";
import { MouseEvent } from "react";

import { DatasetRecord, LineChartDataset } from "../LineChart";
import IChartCollision from "./IChartCollision";

type CollisionInfo = {
  distancePointer: number;
  xMin: number;
  xMax: number;
  record: DatasetRecord;
  recordX: number;
}


// BandCollision will return, at most, a single record from each dataset.  The "band" width is half
// the distance between the closest point to the mouse's position and it's previous/next points.  
// This calculation is a little complex because each dataset can have a different spacing between 
// points.
export default class BandCollision implements IChartCollision {
  private bisect: d3.Bisector<DatasetRecord, number>;
  private distanceMax: number;
  private sortedDatasetRecords: Array<Array<DatasetRecord>>;
  private xScale: d3.ScaleLinear<number, number>;

  constructor(
    datasets: Array<LineChartDataset>,
    xScale: d3.ScaleLinear<number, number>
  ) {
    this.distanceMax = 24;
    this.xScale = xScale;
    this.bisect = d3.bisector((d) => d._x);

    this.sortedDatasetRecords = datasets.map((dataset) => {
      return dataset.records.toSorted((a, b) => {
        if (a._x > b._x) return 1;
        if (a._x < b._x) return -1;
        return 0;
      });
    });
  }
  
  getCollision(event: MouseEvent<SVGRectElement>): Array<DatasetRecord> {
    const pointerX = d3.pointer(event)[0];
    const pointerXValue = this.xScale.invert(pointerX);
    let collisions: Array<CollisionInfo> = [];

    for (const records of this.sortedDatasetRecords) {
      const foundIndex = this.bisect.center(records, pointerXValue);
      const record = records[foundIndex];

      if (this.sortedDatasetRecords.length === 1) {
        return [record];
      }

      const recordX = this.xScale(record._x ?? 0);
      const distancePointer = Math.abs(recordX - pointerX);
      
      let xMin = recordX;
      if (foundIndex !== 0) {
        const previous = records[foundIndex-1];
        const previousX = this.xScale(previous._x ?? 0);
        xMin = (previousX + recordX) / 2;
      }
      let xMax = recordX;
      if (foundIndex !== records.length-1) {
        const next = records[foundIndex+1];
        const nextX = this.xScale(next._x ?? 0);
        xMax = (recordX + nextX) / 2;
      }
      
      collisions.push({
        xMax,
        xMin,
        distancePointer,
        record,
        recordX
      });
    }

    const closest = collisions.reduce((min, current) => {
      if (!min) {
        return current;
      }
      return current.distancePointer < min.distancePointer ? current : min;
    });

    // Filter everything outside the min-max bounds of the closest record
    collisions = collisions.filter((collision) => {
      if (collision.recordX < closest.xMin) {
        return false;
      }
      if (collision.recordX > closest.xMax) {
        return false;
      }
      return true;
    });

    if (collisions.length <= 1) {
      return collisions.map((entry) => entry.record);
    }

    const smallestGap = collisions.reduce((min: number, current: CollisionInfo) => {
      let gap = Math.min(current.recordX - current.xMin, current.xMax - current.recordX);
      if (gap < min) {
        return gap;
      }
      return min;
    }, Infinity);

    // Filter every line, other than the closest one, where distancePointer > smallestGap
    // UNLESS the postions are the same
    collisions = collisions.filter((collision) => {
      if (collision === closest || collision.recordX === closest.recordX) {
        return true;
      }
      return collision.distancePointer < smallestGap;
    });

    collisions.sort((a, b) => a.distancePointer - b.distancePointer);

    return collisions.map((entry) => entry.record);
  }
}